'use client'

import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import { ChangeEvent, RefObject, useEffect, useMemo, useRef, useState } from 'react'
import { useEventListener } from 'usehooks-ts'

import DropdownSelect from '@/components/DropdownSelect'
import ViverseButton from '@/components/ViverseButton'
import WorldSettingBasicTab from '@/components/WorldSettingBasicTab'
import WorldSettingCodeAndCollabTab from '@/components/WorldSettingCodeAndCollabTab'
import WorldSettingLogoOverlayTab from '@/components/WorldSettingLogoOverlayTab'
import WorldSettingMediaTab from '@/components/WorldSettingMediaTab'
import WorldSettingPermissionsTab from '@/components/WorldSettingPermissionsTab'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useAppLoaderContext } from '@/context/AppLoaderContext'
import { useConfigsContext } from '@/context/ConfigsContext'
import { useCreateWorldSettingsDefaultRoomTitle } from '@/hooks/useCreateWorldSettingsDefaultRoomTitle'
import { useFetchTrailerBlendVisionVodInfo } from '@/hooks/useFetchTrailerBlendVisionVodInfo'
import { usePollTrailerBlendVisionVodStatus } from '@/hooks/usePollTrailerBlendVisionVodStatus'
import { useRoomPolicy } from '@/hooks/useRoomPolicy'
import { useScrollOverflowMask } from '@/hooks/useScrollOverflowMask'
import { useUploadImage } from '@/hooks/useUploadImage'
import { useUploadImageFromUrl } from '@/hooks/useUploadImageFromUrl'
import { useUploadMediaToService } from '@/hooks/useUploadMediaToService'
import {
  DEFAULT_PREFERRED_DEVICES,
  DEVICE_LABEL_MAP,
  MAX_CONCURRENT_USERS,
  useWorldSettingsFormSchema,
  WorldSettingsFormValues,
} from '@/hooks/useWorldSettingsFormSchema'
import { createDefaultTrailerAssets, extractIdFromImageUrl, generateFilePreview } from '@/lib/asset'
import { showToast } from '@/lib/toast'
import roomDefaultImage from '@/public/images/room-default.png'
import { deleteFile, deleteVod } from '@/server/services/blendvision'
import { CreateVodResponse } from '@/server/services/types'
import {
  CreateRoomData,
  getRoomPreferredDevices,
  getRoomSettings,
  getRoomTags,
  getStandaloneContent,
  LogoPosition,
  NFTVendor,
  Owner,
  Room,
  TrailerMedia,
  updateTrailerMedia,
} from '@/services/cms'
import { Asset } from '@/types/asset'
import { ImageAsset, Policy } from '@/types/world'
import { zodResolver } from '@hookform/resolvers/zod'
import { motion, useScroll } from 'framer-motion'
import { useForm } from 'react-hook-form'
import useSWR from 'swr'

type SettingTab = 'basic' | 'media' | 'permissions' | 'logoOverlay' | 'codeAndCollab'
const MAX_FILES_VIDEO = 1
const MAX_FILES_IMAGE = 10
const MAX_FILE_SIZE_VIDEO = 25 * 1024 * 1024 // 25MB
const MAX_FILE_SIZE_IMAGE = 2 * 1024 * 1024 // 2MB
const MAX_FILE_SIZE_THUMBNAIL = 2 * 1024 * 1024 // 2MB

export type WorldSettingsDefaultValues = {
  title: string
  description: string
  policy: Policy
  passcode?: string
  concurrentUsers?: number
  genre?: string[]
  enablePasscode?: boolean
  preferredDevices: string[]
  isAdultOnly?: boolean
  logoOverlayPosition?: LogoPosition
  githubUrl?: string
}

type WorldSettingsProps = {
  defaultValues?: WorldSettingsDefaultValues
  defaultThumbnail?: ImageAsset
  sceneSid?: string
  hubSid?: string
  roomId?: string
  owner?: Owner
  onCompleteRoomCreate?: (data: CreateRoomData) => Promise<Room | void>
  onCompleteRoomUpdate?: (data: CreateRoomData) => void
  mode?: 'create' | 'update'
  nftVendor?: NFTVendor
  externalData?: any
  close: () => void
  trailerMedia?: TrailerMedia
  isLoadingTrailerMedia?: boolean
  onAfterCreateWorld?: () => void
}

export const WORLD_SETTINGS_DEFAULT_VALUES: WorldSettingsDefaultValues = {
  title: '',
  description: '',
  policy: 'private',
  passcode: '',
  concurrentUsers: MAX_CONCURRENT_USERS,
  genre: [],
  enablePasscode: false,
  preferredDevices: DEFAULT_PREFERRED_DEVICES,
  isAdultOnly: false,
  logoOverlayPosition: 'top-left',
  githubUrl: '',
}

const createAutoCompleteOptions = <T extends string>(
  options: T[],
  labelMap?: Record<T, string>,
) => {
  return options.map((value) => ({ label: labelMap?.[value] || value, value })) || []
}

export default function WorldSettingPage({
  defaultValues = WORLD_SETTINGS_DEFAULT_VALUES,
  defaultThumbnail,
  sceneSid,
  hubSid,
  roomId,
  // owner,
  onCompleteRoomCreate,
  onCompleteRoomUpdate,
  mode = 'create',
  nftVendor,
  externalData,
  close,
  trailerMedia,
  isLoadingTrailerMedia,
  onAfterCreateWorld,
}: WorldSettingsProps) {
  const t = useTranslations()
  const ROOM_POLICY = useRoomPolicy()
  const { store } = useConfigsContext()
  const { setIsAppLoading } = useAppLoaderContext()
  const { uploadImage } = useUploadImage()
  const { uploadImageFromUrl } = useUploadImageFromUrl()
  const htcToken = store?.credentials?.htcToken

  // ===== basic =====
  const [activeTab, setActiveTab] = useState<SettingTab>('basic')
  const [policy, setPolicy] = useState<keyof typeof ROOM_POLICY>(
    defaultValues.policy || WORLD_SETTINGS_DEFAULT_VALUES.policy,
  )
  const isPublicPolicy = policy === 'public'
  const [showOverlay, setShowOverlay] = useState<boolean>(false)
  const pageScrollRef = useRef<HTMLDivElement>(null)

  // ===== thumbnail =====
  const thumbnailFileInput = useRef<HTMLInputElement>(null)
  const [thumbnail, setThumbnail] = useState<File>()
  const [thumbnailPreview, setThumbnailPreview] = useState<string>()
  const [thumbnailError, setThumbnailError] = useState<Error>()

  // ===== form =====
  const defaultRoomTitle = useCreateWorldSettingsDefaultRoomTitle()
  const formSchema = useWorldSettingsFormSchema()
  const form = useForm<WorldSettingsFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ...defaultValues,
      title: defaultValues.title || defaultRoomTitle,
      enablePasscode: defaultValues.enablePasscode ?? false,
      isAdultOnly: defaultValues.isAdultOnly ?? false,
      logoOverlayPosition: defaultValues.logoOverlayPosition ?? 'top-left',
      trailerMedia: {
        trailer_videos: [],
        trailer_images: [],
      },
    },
  })
  const formValues = form.watch()
  const formErrors = form.formState.errors
  const selectedGenres = formValues.genre || []
  const enablePasscode = formValues.enablePasscode || false
  const concurrentUsers = formValues.concurrentUsers
  const selectedDevices = formValues.preferredDevices || []

  // ===== media =====
  const defaultAssets = useMemo(() => {
    if (!isLoadingTrailerMedia && trailerMedia) {
      return createDefaultTrailerAssets(trailerMedia, true)
    }
    return []
  }, [isLoadingTrailerMedia, trailerMedia])

  const [assets, setAssets] = useState<Asset[]>([])

  useEffect(() => {
    if (!isLoadingTrailerMedia && trailerMedia) {
      form.setValue('trailerMedia', trailerMedia)
    }
  }, [trailerMedia, form, isLoadingTrailerMedia])

  useEffect(() => {
    if (!isLoadingTrailerMedia && defaultAssets?.length > 0) {
      setAssets(defaultAssets)
    }
  }, [isLoadingTrailerMedia, defaultAssets])

  const videoFileInput = useRef<HTMLInputElement>(null)
  const imageFileInput = useRef<HTMLInputElement>(null)
  const uploadMediaToService = useUploadMediaToService()

  useFetchTrailerBlendVisionVodInfo(assets, setAssets, roomId, trailerMedia)

  usePollTrailerBlendVisionVodStatus(assets, setAssets)

  const videoAssets = useMemo(() => {
    return assets.filter((asset) => asset.video?.url || asset.video?.vodId)
  }, [assets])

  const imageAssets = useMemo(() => {
    return assets.filter((asset) => asset.image?.url)
  }, [assets])

  // ===== co-owner =====
  // const [keyword, setKeyword] = useState('')
  // const [searchResult, setSearchResult] = useState<Array<{ id: string; display_name: string }>>([])
  // const [showResult, setShowResult] = useState(false)
  // const [tempCoOwnerList, setTempCoOwnerList] = useState<
  //   Array<{ id: string; display_name: string }>
  // >([])

  // const options = searchResult.map((result) => ({
  //   label: result.display_name,
  //   value: result.id,
  // }))

  // const selected: Array<{ label: string; value: string }> = tempCoOwnerList.map((owner) => ({
  //   label: owner.display_name,
  //   value: owner.id,
  // }))

  // ===== API =====
  const { data: genres, isLoading: isLoadingGenresList } = useSWR('getGenresList', () =>
    getRoomTags(false).then((res) => res?.results?.results),
  )

  const { data: roomSettings, isLoading: isLoadingRoomSettings } = useSWR(
    hubSid ? `getRoomSettings-${hubSid}` : null,
    () => getRoomSettings(hubSid!).then((res) => res?.results),
  )

  const { data: standaloneContent, isLoading: isLoadingStandaloneContent } = useSWR(
    roomSettings &&
      roomSettings?.[0]?.scene?.scene_type === 'standalone' &&
      roomSettings?.[0]?.scene?.scene_sid
      ? `getStandaloneContent-${roomSettings[0].scene.scene_sid}`
      : null,
    () =>
      getStandaloneContent(htcToken!, roomSettings![0].scene.scene_sid).then((res) => res?.results),
  )

  const { data: preferredDevices, isLoading: isLoadingPreferredDevicesList } = useSWR(
    'getPreferredDevicesList',
    () => getRoomPreferredDevices(false).then((res) => res?.results?.results),
  )

  // const { data: coOwners, isLoading: isLoadingCoOwners } = useSWR('getCoOwners', () =>
  //   getCoOwners(htcToken, roomId!).then((res) => res?.results),
  // )

  const showLogoOverlayTab =
    mode === 'update' && roomSettings && roomSettings[0]?.scene?.scene_type === 'standalone'

  const genreOptions = createAutoCompleteOptions(
    (!isLoadingGenresList && genres?.map((genre) => genre.name)) || [],
  )

  const deviceOptions = createAutoCompleteOptions(
    (!isLoadingPreferredDevicesList &&
      preferredDevices?.map((preferredDevice) => preferredDevice.name)) ||
      [],
    DEVICE_LABEL_MAP,
  )

  // ===== co-owner func (todo)=====
  // const searchCoOwner = useMemo(() => {
  // return debounce(async (keyword: string) => {
  //   const result = await getSearchAccounts({ keyword })
  //   if (result?.status === 200 && result.results.results) {
  //     const filteredResults = result.results.results.filter((user) => {
  //       return !tempCoOwnerList.some((coOwner) => coOwner.id === user.id) && user.id !== owner.id
  //     })
  //     setSearchResult(filteredResults)
  //     setShowResult(filteredResults.length > 0)
  //   } else {
  //     setSearchResult([])
  //     setShowResult(false)
  //   }
  // }, 200)
  // }, [tempCoOwnerList, owner.id])

  // const handleSelectCoOwner = (value: string) => {
  //   const selectedCoOwner = searchResult.find((result) => result.id === value)
  //   if (selectedCoOwner && tempCoOwnerList.length < 5) {
  //     setTempCoOwnerList([...tempCoOwnerList, selectedCoOwner])
  //     setKeyword('')
  //   }
  // }

  // const handleRemoveCoOwner = (value: string) => {
  //   setTempCoOwnerList(tempCoOwnerList.filter((owner) => owner.id !== value))
  // }

  // ===== form func =====
  const handlePolicyValueChange = (value: string) => {
    const policySet = Object.entries(ROOM_POLICY).find(([, policy]) => policy.value === value)
    if (!policySet) return

    if (policySet[0] !== 'private') {
      handlePasscodeSwitchChange(false)
      form.setValue('passcode', '')
    }
    setPolicy(policySet[0] as keyof typeof ROOM_POLICY)
    form.setValue('policy', policySet[0] as keyof typeof ROOM_POLICY)
  }

  const handleGenreValueChange = (value: string) => {
    let newSelectedGenres = []
    if (!selectedGenres?.includes(value)) {
      newSelectedGenres = [...selectedGenres, value]
    } else {
      newSelectedGenres = selectedGenres.filter((genre) => genre !== value)
    }
    form.setValue('genre', newSelectedGenres)
    if (form.formState.isSubmitted) {
      form.trigger('genre')
    }
  }

  const handlePasscodeSwitchChange = (checked: boolean) => {
    form.setValue('enablePasscode', checked)
    if (!checked) {
      form.clearErrors('passcode')
    }
  }

  const handleDeviceValueChange = (value: string) => {
    let newSelectedDevices = []
    if (!selectedDevices?.includes(value)) {
      newSelectedDevices = [...selectedDevices, value]
    } else {
      newSelectedDevices = selectedDevices.filter((device) => device !== value)
    }
    form.setValue('preferredDevices', newSelectedDevices)
    if (form.formState.isSubmitted) {
      form.trigger('preferredDevices')
    }
  }

  const handleLogoOverlayPositionChange = (value: LogoPosition) => {
    form.setValue('logoOverlayPosition', value)
    if (form.formState.isSubmitted) {
      form.trigger('logoOverlayPosition')
    }
  }

  // ===== media func =====
  const uploadDone = (index: number) => {
    setAssets((prev) =>
      prev.map((a, i) => {
        return i === index ? { ...a, upload: { ...a.upload!, progress: 100, uploading: false } } : a
      }),
    )
  }

  const uploadStart = (index: number) => {
    setAssets((prev) =>
      prev.map((a, i) => {
        return i === index ? { ...a, upload: { ...a.upload!, uploading: true, progress: 0 } } : a
      }),
    )
  }

  const updateProgress = (progress: number, index: number) => {
    setAssets((prev) =>
      prev.map((a, i) => {
        return i === index ? { ...a, upload: { ...a.upload!, progress } } : a
      }),
    )
  }

  const updateAsset = (result: string | CreateVodResponse, index: number) => {
    setAssets((prev) =>
      prev.map((a, i) => {
        setTimeout(() => {
          setAssets((prev) =>
            prev.map((a, i) => {
              return i === index ? { ...a, upload: { ...a.upload!, uploading: false } } : a
            }),
          )
        }, 500)

        if (i !== index) return a

        const common = {
          ...a,
          upload: { ...a.upload!, progress: 100 },
        }

        return typeof result === 'string'
          ? { ...common, image: { url: result } }
          : { ...common, video: { ...a.video!, vodId: result.vod?.id } }
      }),
    )
  }

  // ===== file =====
  const handleVideoFileChange = (
    event: ChangeEvent<HTMLInputElement>,
    onChange: (files: File[]) => void,
  ) => {
    if (!event.target.files) return

    const video = form.watch('trailerMedia.trailer_videos')
    const files = Array.from(event.target.files)

    if (files.length + (video?.length ?? 0) > MAX_FILES_VIDEO) {
      showToast({
        message: 'You can only upload up to 1 file.',
        type: 'error',
      })
      return
    }

    form.clearErrors('trailerMedia.trailer_videos')

    const validFiles = files.filter((file) => {
      if (file.size > MAX_FILE_SIZE_VIDEO) {
        form.setError('trailerMedia.trailer_videos', {
          message: 'Your media must be less than 25MB.',
        })
        return false
      }

      if (assets.some((asset) => asset.upload?.file?.name === file.name)) {
        return false
      }

      return true
    })

    setAssets((prev) => [...prev, ...validFiles.map(generateFilePreview)])
    onChange(validFiles)
    if (form.formState.isSubmitted) {
      form.trigger('trailerMedia')
    }
  }

  const handleImageFileChange = (
    event: ChangeEvent<HTMLInputElement>,
    onChange: (files: (File | { id?: string; url?: string })[]) => void,
  ) => {
    if (!event.target.files) return

    const image = form.watch('trailerMedia.trailer_images')
    const files = Array.from(event.target.files)

    if (files.length + (image?.length ?? 0) > MAX_FILES_IMAGE) {
      showToast({
        message: 'You can only upload up to 10 file.',
        type: 'error',
      })
      return
    }

    form.clearErrors('trailerMedia.trailer_images')

    const validFiles = files.filter((file) => {
      if (file.size > MAX_FILE_SIZE_IMAGE) {
        form.setError('trailerMedia.trailer_images', {
          message: 'Your media must be less than 2MB.',
        })
        return false
      }

      if (assets.some((asset) => asset.upload?.file?.name === file.name)) {
        return false
      }

      return true
    })

    setAssets((prev) => [...prev, ...validFiles.map(generateFilePreview)])
    onChange([...(image || []), ...validFiles])
    if (form.formState.isSubmitted) {
      form.trigger('trailerMedia')
    }
  }

  const handleDeleteVideo = (index: number, url: string) => {
    setAssets((prev) => prev.filter((asset) => asset.video?.url !== url))
    const video = form.watch('trailerMedia.trailer_videos')

    form.setValue(
      'trailerMedia.trailer_videos',
      video?.filter((_, i) => i !== index),
    )
    form.trigger('trailerMedia.trailer_videos')
    if (videoFileInput.current) {
      videoFileInput.current.value = ''
    }
  }

  const handleDeleteImage = (index: number, url: string) => {
    setAssets((prev) => prev.filter((asset) => asset.image?.url !== url))
    const image = form.watch('trailerMedia.trailer_images')
    form.setValue(
      'trailerMedia.trailer_images',
      image?.filter((_, i) => i !== index),
    )
    form.trigger('trailerMedia.trailer_images')
    if (imageFileInput.current) {
      imageFileInput.current.value = ''
    }
  }

  const handleThumbnailInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return

    const file = event.target.files[0]
    if (file.size > MAX_FILE_SIZE_THUMBNAIL) {
      setThumbnailError(new Error('Your thumbnail must be less than 2MB.'))
    } else {
      setThumbnailError(undefined)
      setThumbnail(file)
      setThumbnailPreview(URL.createObjectURL(file))
    }
  }

  // ===== submit =====
  const isFormSubmitDisabled = useMemo(() => {
    const { title, genre, passcode, trailerMedia } = form.formState.errors
    const formValues = form.getValues()

    if (formValues.policy === 'public') {
      return !!(title || genre || trailerMedia)
    }

    if (formValues.enablePasscode) {
      return !!passcode
    }

    return false
  }, [form, form.formState.errors])

  const handleTrailerMediaUpdate = async (targetRoomId?: string) => {
    const currentRoomId = targetRoomId || roomId
    if (!htcToken || !currentRoomId) return

    const promises = assets.map((asset, index) => {
      if (
        // 🧬 has already uploaded to world-assets
        asset.image &&
        asset.image.url &&
        asset.image.url.toLowerCase().startsWith('https://world-assets')
      ) {
        uploadDone(index)
        return Promise.resolve({ image: asset.image.url })
      }

      // 🧬 has already uploaded to blendvision
      if (asset.video && (asset.video.isConverted || asset.video.vodId)) {
        uploadDone(index)
        return Promise.resolve({ video: asset.video })
      }

      uploadStart(index)

      return uploadMediaToService({
        file: asset.upload!.file!,
        index,
        updateProgress,
      }).then(async (result) => {
        if (!result) return

        updateAsset(result, index)

        return result
      })
    })

    const results = await Promise.all(promises)

    const medias = results
      .filter((result) => result)
      .map((result) => {
        // 🧬 response with image url
        if (typeof result === 'string') {
          return { image: { id: extractIdFromImageUrl(result) } }
        }

        // ✅ already uploaded
        if ('video' in result!) {
          return {
            video: {
              vod_id: result.video.vodId,
              cover_image: result.video.coverImage,
              file_id: result.video.fileId,
            },
          }
        }

        // ✅ already uploaded
        if ('image' in result!) {
          return { image: { id: extractIdFromImageUrl(result.image) } }
        }

        // 🧬 response with vod id
        return {
          video: {
            vod_id: result!.vod.id,
            cover_image: result!.vod.cover_image?.auto.url,
            file_id: result!.vod.source.library.video.id,
          },
        }
      })

    const trailerImages = medias
      .filter((media) => media.image?.id)
      .map((media) => ({ id: media.image!.id! }))
      .filter(Boolean)
    const trailerVideos = medias
      .filter((media) => media.video?.vod_id && media.video?.file_id)
      .map((media) => ({
        vod_id: media.video!.vod_id,
        file_id: media.video!.file_id,
        cover_image: media.video!.cover_image,
      }))
      .filter(Boolean)
    const trailerMedia: TrailerMedia = {
      trailer_videos: trailerVideos,
      trailer_images: trailerImages,
    }

    const deletedMedias = defaultAssets.filter(
      (asset) =>
        asset.video &&
        asset.video.vodId &&
        asset.video.fileId &&
        !assets.some((a) => a.video?.vodId === asset.video?.vodId),
    )

    const promisesDeletedMedias = deletedMedias.map((media) =>
      Promise.all([deleteVod(media.video!.vodId), deleteFile(media.video!.fileId)]),
    )

    await Promise.all([
      updateTrailerMedia(htcToken, currentRoomId, trailerMedia),
      ...promisesDeletedMedias,
    ])
  }

  const onSubmit = async (values: WorldSettingsFormValues) => {
    setShowPopover(true)
    const policyValue = ROOM_POLICY[policy].value

    const isPasscodeEnabled = values.enablePasscode && policyValue !== 'owner_only'
    const isPasscodeDirty = form.getFieldState('passcode').isDirty
    const isValidPasscode = !values.passcode.includes('*')

    const shouldIncludePasscode = isPasscodeEnabled && isPasscodeDirty && isValidPasscode
    const passcode = shouldIncludePasscode
      ? values.passcode
      : values.enablePasscode
        ? undefined
        : ''

    let imageAssetId = ''
    if (thumbnail) {
      const thumbnailUrl = await uploadImage(thumbnail)
      imageAssetId = extractIdFromImageUrl(thumbnailUrl)
    } else if (defaultThumbnail) {
      if (!defaultThumbnail.id && defaultThumbnail.url) {
        const thumbnailUrl = await uploadImageFromUrl(defaultThumbnail.url)
        imageAssetId = extractIdFromImageUrl(thumbnailUrl)
      } else {
        imageAssetId = defaultThumbnail.id
      }
    } else {
      console.error('cannot create room, the image assets id is empty')
      return
    }

    setIsAppLoading(true)
    const roomData = {
      policy: policyValue,
      title: values.title,
      description_plaintext: values.description || '',
      passcode,
      tags: values.genre?.join(',') || '',
      scene_sid: sceneSid || '',
      features: {
        spatial_sound: true,
      },
      image: imageAssetId,
      nft_vendor: nftVendor,
      external_data: externalData,
      max_presence_size: values.concurrentUsers,
      preferred_devices: values.preferredDevices,
      is_adult_only: values.isAdultOnly,
      github_url: values.githubUrl || '',
      ...(values.logoOverlayPosition && {
        logo_overlay_position: values.logoOverlayPosition,
      }),
    }
    try {
      if (mode === 'update') {
        await handleTrailerMediaUpdate()
        await onCompleteRoomUpdate?.(roomData)
      }

      if (mode === 'create') {
        const result = await onCompleteRoomCreate?.(roomData)
        await handleTrailerMediaUpdate(result?.id)
        onAfterCreateWorld?.()
        close()
      }
    } catch (error) {
      console.error('Error saving form:', error)
    }
  }

  // ===== tab scroll mask =====
  const tabScrollRef = useRef<HTMLDivElement>(null)
  const { scrollXProgress } = useScroll({ container: tabScrollRef })
  const tabMaskImage = useScrollOverflowMask({ scrollXProgress })

  // ===== use effect =====
  useEffect(() => {
    if (isLoadingRoomSettings) return
    if (roomSettings) {
      const defaultConcurrentUsers = roomSettings[0]?.max_presence_size || MAX_CONCURRENT_USERS
      form.setValue('concurrentUsers', defaultConcurrentUsers)
    } else {
      form.setValue('concurrentUsers', MAX_CONCURRENT_USERS)
    }
  }, [roomSettings, form, isLoadingRoomSettings])

  useEffect(() => {
    if (defaultValues.enablePasscode) {
      form.setValue('enablePasscode', true)
      form.setValue('passcode', '******')
    }
  }, [form, defaultValues.enablePasscode])

  // useEffect(() => {
  //   if (keyword.length >= 3) {
  //     searchCoOwner(keyword)
  //   } else {
  //     setSearchResult([])
  //     setShowResult(false)
  //   }
  // }, [keyword, searchCoOwner])

  // ===== render =====
  const [showPopover, setShowPopover] = useState(false)

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [])

  const renderRequiredDot = () => {
    return <span className="ml-1 text-red-500">*</span>
  }

  const renderMenuRequiredDot = () => {
    return (
      <span className="absolute top-0 left-full ml-2 inline-block h-[6px] w-[6px] rounded-full bg-red-500 md:relative md:top-auto md:left-auto"></span>
    )
  }

  const hasBasicTabRequiredErrors = formErrors.title || formErrors.genre
  const hasMediaTabRequiredErrors = formErrors.trailerMedia
  const hasPermissionsTabRequiredErrors = formErrors.passcode
  const isStandalone = roomSettings && roomSettings[0]?.scene?.scene_type === 'standalone'
  const isAccessDropdownEnabled =
    !isLoadingRoomSettings &&
    (!isStandalone || (!isLoadingStandaloneContent && !!standaloneContent?.publish_url))

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <WorldSettingBasicTab
            genreOptions={genreOptions}
            deviceOptions={deviceOptions}
            handleGenreValueChange={handleGenreValueChange}
            handleDeviceValueChange={handleDeviceValueChange}
            handleInputChange={handleThumbnailInputChange}
            inputRef={thumbnailFileInput}
            thumbnailPreview={thumbnailPreview}
            uploadError={thumbnailError}
            defaultThumbnail={defaultThumbnail}
            roomDefaultImage={roomDefaultImage.src}
            isPublicPolicy={isPublicPolicy}
            renderRequiredDot={renderRequiredDot}
            selectedGenres={selectedGenres}
            selectedDevices={selectedDevices}
          />
        )
      case 'media':
        return (
          <WorldSettingMediaTab
            isPublicPolicy={isPublicPolicy}
            renderRequiredDot={renderRequiredDot}
            isLoadingTrailerMedia={isLoadingTrailerMedia}
            videoFileInput={videoFileInput}
            imageFileInput={imageFileInput}
            videoAssets={videoAssets}
            imageAssets={imageAssets}
            handleVideoFileChange={handleVideoFileChange}
            handleImageFileChange={handleImageFileChange}
            handleDeleteVideo={handleDeleteVideo}
            handleDeleteImage={handleDeleteImage}
          />
        )
      case 'permissions':
        return (
          <WorldSettingPermissionsTab
            policy={policy}
            enablePasscode={enablePasscode}
            concurrentUsers={concurrentUsers}
            handlePasscodeSwitchChange={handlePasscodeSwitchChange}
            isLoadingRoomSettings={isLoadingRoomSettings}
            externalData={externalData}
          />
        )
      case 'logoOverlay':
        return (
          <WorldSettingLogoOverlayTab
            handleLogoOverlayPositionChange={handleLogoOverlayPositionChange}
          />
        )
      case 'codeAndCollab':
        return <WorldSettingCodeAndCollabTab />
      default:
        return null
    }
  }

  useEventListener(
    'scroll',
    (event) => {
      const { scrollTop } = event.currentTarget as HTMLElement
      setShowOverlay(scrollTop > 0)
    },
    pageScrollRef as RefObject<HTMLElement>,
  )

  const handleWheel = (event: WheelEvent) => {
    if (pageScrollRef.current) {
      event.preventDefault()
      pageScrollRef.current.scrollTop += event.deltaY
    }
  }

  useEventListener('wheel', handleWheel)
  return (
    <div className="bg-vive-surface-normal/60 1.5xl:h-[calc(100dvh-80px)] 1.5xl:top-20 fixed top-18 left-0 z-39 flex h-[calc(100dvh-72px)] w-full items-start justify-center backdrop-blur-[100px] md:top-[82px] md:h-[calc(100dvh-82px)]">
      <div className="1.5xl:pt-12 1.5xl:pb-[32px] relative flex h-full w-full max-w-[904px] flex-col items-start justify-center p-4 md:px-6 md:pt-8">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex h-full w-full flex-col items-center overflow-auto"
          >
            <div className="sticky flex w-full items-center justify-between pb-4 md:pb-6">
              <p className="subheader-1 md:header-4">{t('profile-page.my-worlds-card-settings')}</p>
              <div className="flex items-center justify-center gap-2">
                <ViverseButton
                  type="button"
                  variant="secondary"
                  size="medium"
                  onClick={close}
                  className="min-w-[80px] px-4 py-2 text-[14px]"
                >
                  {t('profile-page.my-worlds-card-settings-cancel-button')}
                </ViverseButton>
                <Popover open={isFormSubmitDisabled && showPopover}>
                  <PopoverTrigger asChild>
                    <ViverseButton
                      type="submit"
                      variant="default"
                      size="medium"
                      className="min-w-[80px] px-4 py-2 text-[14px]"
                      onClick={() => setShowPopover(true)}
                    >
                      {mode === 'create'
                        ? t('profile-page.my-worlds-card-settings-create-button')
                        : t('profile-page.my-worlds-card-settings-save-button')}
                    </ViverseButton>
                  </PopoverTrigger>
                  <PopoverContent
                    align="end"
                    sideOffset={8}
                    showArrow={true}
                    className="bg-vive-primary-70 text-vive-primary-20 caption left-20 z-1001 w-65 border-0 p-4"
                    arrowClassName="fill-vive-primary-70 w-6 h-3"
                    onInteractOutside={() => setShowPopover(false)}
                  >
                    {t('profile-page.my-worlds-card-settings-incomplete-tips')}
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div
              className="flex h-full w-full flex-col gap-2 overflow-scroll md:gap-8"
              ref={pageScrollRef}
              style={{
                maskImage: showOverlay
                  ? 'linear-gradient(to bottom, transparent 0%, black 15%, black 100%)'
                  : 'none',
                WebkitMaskImage: showOverlay
                  ? 'linear-gradient(to bottom, transparent 0%, black 15%, black 100%)'
                  : 'none',
              }}
            >
              <FormField
                control={form.control}
                name="policy"
                render={({ field }) => (
                  <FormItem className="flex w-full flex-col">
                    <FormLabel className="subheader-2 text-[14px] md:text-[16px]">
                      {t('profile-page.my-worlds-card-settings-access')}
                    </FormLabel>
                    <FormControl>
                      <DropdownSelect
                        onValueChange={handlePolicyValueChange}
                        disabled={!isAccessDropdownEnabled}
                        {...field}
                      >
                        <DropdownSelect.Trigger>
                          <DropdownSelect.Value
                            asChild
                            title={ROOM_POLICY[policy].title}
                            description={ROOM_POLICY[policy].description}
                            Icon={ROOM_POLICY[policy].Icon}
                          />
                        </DropdownSelect.Trigger>
                        <DropdownSelect.Content>
                          {Object.entries(ROOM_POLICY).map(
                            ([key, { value, title, description, Icon }]) => (
                              <DropdownSelect.Item
                                key={`dropdown-select-item-${value}`}
                                value={value}
                                title={title}
                                Icon={Icon}
                                description={description}
                                selected={key === policy}
                              />
                            ),
                          )}
                        </DropdownSelect.Content>
                      </DropdownSelect>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex w-full flex-1 flex-col md:flex-row">
                <motion.div
                  className="no-scrollbar border-vive-primary-70 scrollbar-hide w-full overflow-scroll overflow-x-auto md:w-[198px] md:border-r"
                  ref={tabScrollRef}
                  style={{ maskImage: tabMaskImage }}
                >
                  <div className="flex flex-row flex-nowrap md:flex-col md:gap-4">
                    <div
                      className={cn(
                        'subheader-3 text-vive-primary-20 relative w-full cursor-pointer items-center rounded-lg p-3 text-center text-[14px] whitespace-nowrap transition-colors md:text-left md:text-[16px] md:whitespace-normal',
                        activeTab === 'basic'
                          ? 'before:bg-vive-brand-50 before:transform(t) after:bg-vive-brand-50 before:absolute before:top-1/2 before:left-0 before:hidden before:h-6 before:w-1 before:-translate-y-1/2 before:rounded-[3px] before:content-[""] after:absolute after:bottom-0 after:left-1/2 after:h-1 after:w-full after:-translate-x-1/2 after:rounded-[3px] after:content-[""] md:before:block md:after:hidden'
                          : 'text-vive-primary-20',
                      )}
                      onClick={() => setActiveTab('basic')}
                    >
                      <span className="relative">
                        {t('profile-page.my-worlds-card-settings-tab-details')}
                        {hasBasicTabRequiredErrors && renderMenuRequiredDot()}
                      </span>
                    </div>
                    <div
                      className={cn(
                        'subheader-3 text-vive-primary-20 relative w-full cursor-pointer items-center rounded-lg p-3 text-center text-[14px] whitespace-nowrap transition-colors md:text-left md:text-[16px] md:whitespace-normal',
                        activeTab === 'media'
                          ? 'before:bg-vive-brand-50 before:transform(t) after:bg-vive-brand-50 before:absolute before:top-1/2 before:left-0 before:hidden before:h-6 before:w-1 before:-translate-y-1/2 before:rounded-[3px] before:content-[""] after:absolute after:bottom-0 after:left-1/2 after:h-1 after:w-full after:-translate-x-1/2 after:rounded-[3px] after:content-[""] md:before:block md:after:hidden'
                          : 'text-vive-primary-20',
                      )}
                      onClick={() => setActiveTab('media')}
                    >
                      <span className="relative w-fit">
                        {t('profile-page.my-worlds-card-settings-tab-medias')}
                        {hasMediaTabRequiredErrors && renderMenuRequiredDot()}
                      </span>
                    </div>
                    <div
                      className={cn(
                        'subheader-3 text-vive-primary-20 relative w-full cursor-pointer items-center rounded-lg p-3 text-center text-[14px] whitespace-nowrap transition-colors md:text-left md:text-[16px] md:whitespace-normal',
                        activeTab === 'codeAndCollab'
                          ? 'before:bg-vive-brand-50 before:transform(t) after:bg-vive-brand-50 before:absolute before:top-1/2 before:left-0 before:hidden before:h-6 before:w-1 before:-translate-y-1/2 before:rounded-[3px] before:content-[""] after:absolute after:bottom-0 after:left-1/2 after:h-1 after:w-full after:-translate-x-1/2 after:rounded-[3px] after:content-[""] md:before:block md:after:hidden'
                          : 'text-vive-primary-20',
                      )}
                      onClick={() => setActiveTab('codeAndCollab')}
                    >
                      <span className="relative w-fit">
                        {t('profile-page.my-worlds-card-settings-tab-code-and-collab')}
                      </span>
                    </div>
                    {showLogoOverlayTab && (
                      <div
                        className={cn(
                          'subheader-3 text-vive-primary-20 relative w-full cursor-pointer items-center rounded-lg p-3 text-center text-[14px] whitespace-nowrap transition-colors md:text-left md:text-[16px] md:whitespace-normal',
                          activeTab === 'logoOverlay'
                            ? 'before:bg-vive-brand-50 before:transform(t) after:bg-vive-brand-50 before:absolute before:top-1/2 before:left-0 before:hidden before:h-6 before:w-1 before:-translate-y-1/2 before:rounded-[3px] before:content-[""] after:absolute after:bottom-0 after:left-1/2 after:h-1 after:w-full after:-translate-x-1/2 after:rounded-[3px] after:content-[""] md:before:block md:after:hidden'
                            : 'text-vive-primary-20',
                        )}
                        onClick={() => setActiveTab('logoOverlay')}
                      >
                        <span className="relative w-fit">
                          {t('profile-page.my-worlds-card-settings-tab-logo-overlay')}
                        </span>
                      </div>
                    )}
                    <div
                      className={cn(
                        'subheader-3 text-vive-primary-20 relative w-full items-center rounded-lg p-3 text-center text-[14px] whitespace-nowrap transition-colors md:text-left md:text-[16px] md:whitespace-normal',
                        activeTab === 'permissions'
                          ? 'before:bg-vive-brand-50 before:transform(t) after:bg-vive-brand-50 before:absolute before:top-1/2 before:left-0 before:hidden before:h-6 before:w-1 before:-translate-y-1/2 before:rounded-[3px] before:content-[""] after:absolute after:bottom-0 after:left-1/2 after:h-1 after:w-full after:-translate-x-1/2 after:rounded-[3px] after:content-[""] md:before:block md:after:hidden'
                          : 'text-vive-primary-20',
                      )}
                      onClick={() => setActiveTab('permissions')}
                    >
                      <span className="relative w-fit">
                        {t('profile-page.my-worlds-card-settings-tab-permissions')}
                        {hasPermissionsTabRequiredErrors && renderMenuRequiredDot()}
                      </span>
                    </div>
                  </div>
                </motion.div>
                <div className="flex-1 overflow-auto pt-4 md:ml-8 md:pt-0">
                  {renderTabContent()}
                </div>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </div>
  )
}
